'use client'

import type { Choice, MultiStepQuiz, Step } from '@/features/quiz/quiz'
import {
  softEditorialEase,
  softEditorialEaseInOut,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import clsx from 'clsx'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import NProgress from 'nprogress'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState, type ReactNode } from 'react'

type Props = {
  quiz: MultiStepQuiz
}

export default function QuizForm({ quiz }: Props) {
  const pathname = usePathname()
  const params = useSearchParams()
  const router = useRouter()
  const prefersReducedMotion = useReducedMotion()
  const steps = (params.get('step') ?? 'root').split(',')
  const stepKey = steps[steps.length - 1]
  const step = quiz.stepMap[stepKey] ?? quiz.stepMap.root

  const [direction, setDirection] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<string | null>(
    null,
  )

  useEffect(() => {
    setIsLoading(false)
    setSelectedTreatmentId(null)
  }, [stepKey])

  function setSteps(newSteps: string[]) {
    const nextParams = new URLSearchParams(window.location.search)
    nextParams.set('step', newSteps.join(','))
    router.push(`${pathname}?${nextParams.toString()}`)
  }

  function openChoice(choice: Choice) {
    if (isLoading) return

    if (choice.href) {
      setIsLoading(true)
      NProgress.start()
      router.push(choice.href)
      return
    }

    setDirection(1)
    setSteps([...steps, choice.id])
  }

  function goBack() {
    if (isLoading || steps.length <= 1) return

    setDirection(-1)
    setSteps(steps.slice(0, -1))
  }

  function startTreatment() {
    const selectedChoice = step.choices.find(
      (choice) => choice.id === selectedTreatmentId,
    )

    if (selectedChoice) openChoice(selectedChoice)
  }

  return (
    <div className="grid min-h-[100dvh] min-h-screen overflow-x-clip bg-white pt-[66px] font-sans text-[#0B0B0F] lg:pt-[86px]">
      <AnimatePresence mode="sync" initial={true} custom={direction}>
        <motion.main
          key={stepKey}
          custom={direction}
          className="col-start-1 row-start-1 flex min-h-[calc(100dvh-66px)] w-full flex-col bg-white lg:min-h-[calc(100dvh-86px)]"
          initial={
            prefersReducedMotion
              ? false
              : { opacity: 0, x: direction > 0 ? 8 : -8 }
          }
          animate={{
            opacity: 1,
            x: 0,
            transition: prefersReducedMotion
              ? { duration: 0 }
              : {
                  delay: 0.08,
                  duration: softMotionDuration.page,
                  ease: softEditorialEase,
                },
          }}
          exit={
            prefersReducedMotion
              ? { opacity: 1, transition: { duration: 0 } }
              : {
                  opacity: 0,
                  x: direction > 0 ? -6 : 6,
                  transition: {
                    duration: 0.34,
                    ease: softEditorialEaseInOut,
                  },
                }
          }
        >
          {step.layout === 'landing' ? (
            <LandingStep
              step={step}
              isLoading={isLoading}
              onChoose={openChoice}
              prefersReducedMotion={Boolean(prefersReducedMotion)}
            />
          ) : step.layout === 'treatments' ? (
            <TreatmentStep
              step={step}
              isLoading={isLoading}
              selectedChoiceId={selectedTreatmentId}
              onSelect={setSelectedTreatmentId}
              onStart={startTreatment}
              onBack={goBack}
              prefersReducedMotion={Boolean(prefersReducedMotion)}
            />
          ) : (
            <StandardStep
              step={step}
              isLoading={isLoading}
              onChoose={openChoice}
              onBack={goBack}
              prefersReducedMotion={Boolean(prefersReducedMotion)}
            />
          )}
        </motion.main>
      </AnimatePresence>
    </div>
  )
}

function LandingStep({
  step,
  isLoading,
  onChoose,
  prefersReducedMotion,
}: {
  step: Step
  isLoading: boolean
  onChoose: (choice: Choice) => void
  prefersReducedMotion: boolean
}) {
  return (
    <>
      <section className="flex flex-col items-center px-5 pb-8 pt-11 lg:px-[140px] lg:pb-9 lg:pt-20">
        <div className="flex w-full max-w-[680px] flex-col gap-3 lg:gap-4">
          <Eyebrow>{step.eyebrow ?? 'Request care'}</Eyebrow>
          <h1 className="font-jakarta text-[30px] font-bold leading-[36px] tracking-[-0.02em] lg:text-[34px] lg:leading-[42px]">
            {step.question.title}
          </h1>
          {step.question.subtitle && (
            <p className="text-[14px] leading-[21px] text-[#82828B] lg:text-[14.5px] lg:leading-[22px]">
              {step.question.subtitle}
            </p>
          )}
        </div>
      </section>

      <section
        className="mx-auto flex w-full max-w-[680px] flex-col gap-3 px-5 lg:px-0"
        aria-label="Care options"
      >
        {step.choices.map((choice, index) => (
          <LandingChoice
            key={choice.id}
            choice={choice}
            index={index}
            disabled={isLoading}
            onChoose={onChoose}
            prefersReducedMotion={prefersReducedMotion}
          />
        ))}
      </section>

      <section className="flex w-full flex-1 bg-white">
        <div className="mx-auto flex w-full max-w-[680px] flex-col gap-2 px-5 pb-16 pt-8 lg:px-0 lg:pb-24">
          {step.notice && (
            <p className="text-[13px] leading-[19px] text-[#5C5C64] lg:text-[13.5px]">
              {step.notice}
            </p>
          )}
          {step.legal && (
            <p className="text-[12px] leading-[17px] text-[#A0A0A8] lg:text-[12.5px]">
              {step.legal}
            </p>
          )}
        </div>
      </section>
    </>
  )
}

function LandingChoice({
  choice,
  index,
  disabled,
  onChoose,
  prefersReducedMotion,
}: {
  choice: Choice
  index: number
  disabled: boolean
  onChoose: (choice: Choice) => void
  prefersReducedMotion: boolean
}) {
  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={() => onChoose(choice)}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ amount: 0.3, once: true }}
      transition={{
        delay: prefersReducedMotion ? 0 : 0.12 + Math.min(index, 5) * 0.065,
        duration: 0.82,
        ease: softEditorialEase,
      }}
      className="focus-visible:outline-offset-3 group flex min-h-[82px] w-full items-center gap-3.5 rounded-[18px] bg-[#F4F4F6] px-[18px] py-[18px] text-left transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#EDEDF0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563EB] active:bg-[#EDEDF0] disabled:cursor-wait lg:rounded-[20px] lg:px-[22px] lg:py-5"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[16px] font-bold leading-[21px] tracking-[-0.01em] lg:text-[17px] lg:leading-[22px]">
          {choice.title}
        </span>
        {choice.subtitle && (
          <span className="text-[13px] leading-[18px] text-[#82828B] lg:text-[13.5px]">
            {choice.subtitle}
          </span>
        )}
      </span>
      {choice.price && (
        <span className="ml-auto flex h-[34px] shrink-0 flex-col items-end justify-end gap-px">
          {choice.pricePrefix && (
            <span className="text-[11px] font-medium leading-[14px] text-[#A0A0A8] lg:text-[11.5px]">
              {choice.pricePrefix}
            </span>
          )}
          <span className="text-[14px] font-semibold leading-[18px] tracking-[-0.01em] text-[#0B0B0F] lg:text-[15px] lg:leading-[19px]">
            {choice.price}
          </span>
        </span>
      )}
    </motion.button>
  )
}

function TreatmentStep({
  step,
  isLoading,
  selectedChoiceId,
  onSelect,
  onStart,
  onBack,
  prefersReducedMotion,
}: {
  step: Step
  isLoading: boolean
  selectedChoiceId: string | null
  onSelect: (choiceId: string) => void
  onStart: () => void
  onBack: () => void
  prefersReducedMotion: boolean
}) {
  return (
    <>
      <StepHeading step={step} />
      <section
        className="mx-auto flex w-full max-w-[680px] flex-col gap-3 px-5 lg:px-0"
        aria-label="Treatment plans"
        role="radiogroup"
      >
        {step.choices.map((choice, index) => {
          const selected = choice.id === selectedChoiceId

          return (
            <motion.button
              type="button"
              role="radio"
              aria-checked={selected}
              key={choice.id}
              disabled={isLoading}
              onClick={() => onSelect(choice.id)}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: prefersReducedMotion ? 0 : 0.12 + index * 0.065,
                duration: 0.76,
                ease: softEditorialEase,
              }}
              className={clsx(
                'focus-visible:outline-offset-3 flex min-h-[95px] items-center gap-3 rounded-[18px] border-[1.5px] bg-[#F4F4F6] p-[18px] text-left transition-[border-color,background-color] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563EB] disabled:cursor-wait lg:min-h-[82px] lg:gap-3.5 lg:rounded-[20px] lg:px-[22px] lg:py-5',
                selected
                  ? 'border-[#0B0B0F]'
                  : 'border-transparent hover:bg-[#EDEDF0] active:bg-[#EDEDF0]',
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[16px] font-bold leading-[21px] tracking-[-0.01em] lg:text-[17px] lg:leading-[22px]">
                  {choice.title}
                </span>
                {choice.subtitle && (
                  <span className="text-[13px] leading-[18px] text-[#82828B] lg:text-[13.5px]">
                    {choice.subtitle}
                  </span>
                )}
              </span>
              <span
                aria-hidden="true"
                className={clsx(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                  selected
                    ? 'border-[#0B0B0F] bg-[#0B0B0F]'
                    : 'border-[#C4C4CA] bg-white',
                )}
              >
                {selected && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
            </motion.button>
          )
        })}

        {step.footer && (
          <div className="flex min-h-14 items-center justify-center rounded-[18px] border-[1.5px] border-dashed border-[#DADAE0] px-[18px] py-4 text-[13.5px] font-semibold text-[#A0A0A8] lg:min-h-[61px] lg:rounded-[20px] lg:text-sm">
            {step.footer}
          </div>
        )}
      </section>

      <StepActions
        primaryLabel="Start my plan →"
        primaryDisabled={!selectedChoiceId || isLoading}
        onPrimary={onStart}
        onBack={onBack}
      />
    </>
  )
}

function StandardStep({
  step,
  isLoading,
  onChoose,
  onBack,
  prefersReducedMotion,
}: {
  step: Step
  isLoading: boolean
  onChoose: (choice: Choice) => void
  onBack: () => void
  prefersReducedMotion: boolean
}) {
  return (
    <>
      <StepHeading step={step} />
      <section
        className="mx-auto flex w-full max-w-[680px] flex-col gap-3 px-5 lg:px-0"
        aria-label="Choose an option"
      >
        {step.choices.map((choice, index) => (
          <motion.button
            type="button"
            key={`${choice.id}-${index}`}
            disabled={isLoading}
            onClick={() => onChoose(choice)}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: prefersReducedMotion ? 0 : 0.12 + index * 0.065,
              duration: 0.76,
              ease: softEditorialEase,
            }}
            className="focus-visible:outline-offset-3 group flex min-h-[82px] items-center gap-3.5 rounded-[20px] bg-[#F4F4F6] px-[22px] py-5 text-left transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#EDEDF0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563EB] active:bg-[#EDEDF0] disabled:cursor-wait"
          >
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-[16px] font-bold leading-[21px] tracking-[-0.01em] lg:text-[17px] lg:leading-[22px]">
                {choice.title}
              </span>
              {choice.subtitle && (
                <span className="text-[13px] leading-[18px] text-[#82828B] lg:text-[13.5px]">
                  {choice.subtitle}
                </span>
              )}
              {choice.price && (
                <span className="mt-1 text-[14px] font-bold leading-[18px] tracking-[-0.01em]">
                  {choice.pricePrefix && `${choice.pricePrefix} `}
                  {choice.price}
                </span>
              )}
            </span>
            <ArrowRight className="text-[#C9C9CF] transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] group-hover:text-[#0B0B0F] group-active:text-[#0B0B0F]" />
          </motion.button>
        ))}
      </section>

      <StepActions onBack={onBack} />
    </>
  )
}

function StepHeading({ step }: { step: Step }) {
  return (
    <section className="mx-auto flex w-full max-w-[680px] flex-col items-start gap-3 px-5 pb-6 pt-10 lg:gap-4 lg:px-0 lg:pb-9 lg:pt-20">
      <Eyebrow>{step.eyebrow ?? 'Request care'}</Eyebrow>
      <h1
        className={clsx(
          'max-w-[640px] text-[26px] font-bold leading-[33px] tracking-[-0.02em] lg:font-jakarta lg:text-[34px] lg:leading-[42px]',
          step.layout === 'treatments' && 'lg:min-h-[84px]',
        )}
      >
        {step.question.title}
      </h1>
    </section>
  )
}

function StepActions({
  primaryLabel,
  primaryDisabled = false,
  onPrimary,
  onBack,
}: {
  primaryLabel?: string
  primaryDisabled?: boolean
  onPrimary?: () => void
  onBack: () => void
}) {
  return (
    <section className="mt-auto flex justify-center px-5 pb-14 pt-8 lg:px-[140px] lg:pb-20 lg:pt-10">
      <div className="flex w-full max-w-[335px] flex-col items-center gap-2.5 lg:w-auto lg:max-w-none lg:flex-row-reverse lg:gap-3">
        {primaryLabel && onPrimary && (
          <button
            type="button"
            disabled={primaryDisabled}
            onClick={onPrimary}
            className="focus-visible:outline-offset-3 flex w-full items-center justify-center rounded-full bg-[#0B0B0F] px-7 py-3.5 text-[15px] font-semibold leading-[19px] text-white transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#242429] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563EB] disabled:cursor-not-allowed disabled:bg-[#DADAE0] disabled:text-[#82828B] lg:w-auto lg:py-[13px] lg:text-[14.5px] lg:leading-[18px]"
          >
            {primaryLabel}
          </button>
        )}
        <button
          type="button"
          onClick={onBack}
          className="focus-visible:outline-offset-3 flex w-full items-center justify-center gap-[9px] rounded-full border-[1.5px] border-[#0B0B0F]/20 px-[26px] py-[13px] text-[14.5px] font-semibold leading-[18px] transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:border-[#0B0B0F]/45 hover:bg-[#F4F4F6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563EB] lg:w-auto"
        >
          <ArrowLeft />
          Back
        </button>
      </div>
    </section>
  )
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#1F8A70]" />
      <span className="text-[11.5px] font-semibold uppercase leading-[14px] tracking-[0.025em] text-[#1F8A70] lg:text-[13px] lg:leading-4">
        {children}
      </span>
    </div>
  )
}

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={clsx('h-[18px] w-[18px] shrink-0', className)}
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M5 12h14m-6-6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ArrowLeft() {
  return (
    <svg
      aria-hidden="true"
      className="h-[15px] w-[15px] shrink-0"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M19 12H5m7 7-7-7 7-7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
